import React, { useState, useEffect } from 'react';
import { X, FileDown, Calculator, Loader2, User, DollarSign, RefreshCw, ChevronDown, ChevronUp, Copy, Briefcase, Building, Fingerprint, MapPin, Globe, GraduationCap, ChevronRight, ArrowRight, ArrowLeft, Zap, Edit3, ArrowDown, ChevronsDown, FileText, Download, Sparkles, Upload, TrendingUp, AlertCircle, Layers, Tag, CreditCard } from 'lucide-react';
import { GoogleGenAI, Type, ThinkingLevel } from "@google/genai";
import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';
import { saveUsageData } from '../services/usageService';
import { addWatermarkToPdf } from '../lib/pdfUtils';
import { LoadingOverlay } from './LoadingOverlay';

// Set worker source for pdfjs via unpkg CDN to avoid MIME type issues on custom servers
// Using 4.4.168 which is more stable for mobile browsers
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs`;

interface ArrearCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAutomatic?: boolean;
  isExperiment?: boolean;
}

interface MonthlyData {
  month: string;
  monthLabel: string;
  basic: string;
  da: string;
  daPercent: string;
  hra: string;
  hraPercent: string;
  medical: string;
  nps: string;
  gic: string;
  tds: string;
  isExpanded: boolean;
  incrementApplicable?: boolean;
}

const DA_RATES = [
    // 2023
    { month: '2023-06', rate: 46 }, { month: '2023-07', rate: 46 }, { month: '2023-08', rate: 46 }, { month: '2023-09', rate: 46 },
    { month: '2023-10', rate: 46 }, { month: '2023-11', rate: 46 }, { month: '2023-12', rate: 46 },
    // 2024
    { month: '2024-01', rate: 50 }, { month: '2024-02', rate: 50 }, { month: '2024-03', rate: 50 },
    { month: '2024-04', rate: 50 }, { month: '2024-05', rate: 50 }, { month: '2024-06', rate: 50 },
    { month: '2024-07', rate: 53 }, { month: '2024-08', rate: 53 }, { month: '2024-09', rate: 53 },
    { month: '2024-10', rate: 53 }, { month: '2024-11', rate: 53 }, { month: '2024-12', rate: 53 },
    // 2025
    { month: '2025-01', rate: 55 }, { month: '2025-02', rate: 55 }, { month: '2025-03', rate: 55 },
    { month: '2025-04', rate: 55 }, { month: '2025-05', rate: 55 }, { month: '2025-06', rate: 55 },
    { month: '2025-07', rate: 58 }, { month: '2025-08', rate: 58 }, { month: '2025-09', rate: 58 },
    { month: '2025-10', rate: 58 }, { month: '2025-11', rate: 58 }, { month: '2025-12', rate: 58 },
    // 2026
    { month: '2026-01', rate: 60 }, { month: '2026-02', rate: 60 }, { month: '2026-03', rate: 60 },
    { month: '2026-04', rate: 60 }, { month: '2026-05', rate: 60 }, { month: '2026-06', rate: 60 },
    { month: '2026-07', rate: 60 }, { month: '2026-08', rate: 60 }, { month: '2026-09', rate: 60 },
    { month: '2026-10', rate: 60 }, { month: '2026-11', rate: 60 }, { month: '2026-12', rate: 60 }
];

const DA_OPTIONS = [46, 50, 53, 55, 58, 60];
const HRA_OPTIONS_OLD = [4, 6, 8, 16];
const HRA_OPTIONS_NEW = [5, 7.5, 10, 20];
const RECRUITMENT_PHASES = ["BPSC TRE-1", "BPSC TRE-2", "BPSC TRE-1 (SUPPLIMENTRY)", "BPSC TRE-3", "HEADMASTER", "HEAD TEACHER", "EXCLUSIVE", "ASSISTANT TEACHER(NIYOJIT)"];
const GRADE_LEVELS = ["1-5", "6-8", "9-10", "11-12"];

export const ArrearCalculatorModal: React.FC<ArrearCalculatorModalProps> = ({ isOpen, onClose, isAutomatic = false, isExperiment = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  useEffect(() => {
    const modalContent = document.getElementById('modal-content');
    if (modalContent) {
      modalContent.scrollTo(0, 0);
    }
  }, [currentStep]);

  const [claimedPayMode, setClaimedPayMode] = useState<'auto' | 'manual'>(isAutomatic ? 'auto' : 'auto');
  const [selectedPhase, setSelectedPhase] = useState(RECRUITMENT_PHASES[0]);
  const [phaseSuffix, setPhaseSuffix] = useState('');
  const [selectedGrade, setSelectedGrade] = useState(GRADE_LEVELS[0]);
  const [designation, setDesignation] = useState('');
  const [udiseCode, setUdiseCode] = useState('');
  const [udiseStatus, setUdiseStatus] = useState<'none' | 'valid' | 'invalid' | 'searching'>('none');

  const [tName, setTName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [blockName, setBlockName] = useState('');
  const [pranNo, setPranNo] = useState('');
  const [panNo, setPanNo] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [subject, setSubject] = useState('');
  const [joiningDate, setJoiningDate] = useState('');
  const [startDate, setStartDate] = useState('2024-01');
  const [endDate, setEndDate] = useState('2024-06');
  
  const [financialYear, setFinancialYear] = useState('FY 2023-2024');
  const [salarySlipFiles, setSalarySlipFiles] = useState<Record<string, File | null>>({
    'FY 2023-2024': null,
    'FY 2024-2025': null,
    'FY 2025-2026': null,
    'FY 2026-2027': null
  });
  
  const [hraIncreaseMonth, setHraIncreaseMonth] = useState('');
  const [hraIncreasePercent, setHraIncreasePercent] = useState<number | ''>('');
  
  const [monthlyDue, setMonthlyDue] = useState<MonthlyData[]>([]);
  const [monthlyDrawn, setMonthlyDrawn] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [parsingYears, setParsingYears] = useState<string[]>([]);
  const [debugData, setDebugData] = useState<any>(null);
  const [extractionMethod, setExtractionMethod] = useState<string | null>(null);
  const [extractedDetailsPerFY, setExtractedDetailsPerFY] = useState<Record<string, {name: string, pran: string, designation: string}>>({});
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [extractedName, setExtractedName] = useState('');
  const [nameKeyword, setNameKeyword] = useState<string | null>(null);
  const [extractedPran, setExtractedPran] = useState('');
  const [pranExtractionMethod, setPranExtractionMethod] = useState<string | null>(null);
  const [pranKeyword, setPranKeyword] = useState<string | null>(null);
  const [phaseExtractionMethod, setPhaseExtractionMethod] = useState<string | null>(null);
  const [phaseKeyword, setPhaseKeyword] = useState<string | null>(null);
  const [designationExtractionMethod, setDesignationExtractionMethod] = useState<string | null>(null);
  const [designationKeyword, setDesignationKeyword] = useState<string | null>(null);
  const [monthlyKeywords, setMonthlyKeywords] = useState<Record<string, string>>({});
  const [monthlyExtractionSource, setMonthlyExtractionSource] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setSalarySlipFiles({
        'FY 2023-2024': null,
        'FY 2024-2025': null,
        'FY 2025-2026': null,
        'FY 2026-2027': null
      });
      setDebugData(null);
      setExtractionMethod(null);
      setExtractedDetailsPerFY({});
      setExtractionError(null);
      setExtractedName('');
      setHraIncreaseMonth('');
      setHraIncreasePercent('');
    }
  }, [isOpen]);

  const parseSalarySlipPDF = async (file: File, fy?: string) => {
    if (fy) setParsingYears(prev => [...prev, fy]);
    setExtractedName('');
    setNameKeyword(null);
    setExtractedPran('');
    setPranNo('');
    setPranExtractionMethod(null);
    setPranKeyword(null);
    setMonthlyKeywords({});
    setMonthlyExtractionSource(null);
    setPhaseSuffix('');
    
    const updateDebugDataState = (newData: any) => {
      setDebugData((prev: any) => {
        if (!prev) return newData;
        const mergedEarnings = [...(prev.earnings || [])];
        
        const earningsMap = new Map();
        mergedEarnings.forEach((e: any) => earningsMap.set(e.month, e));
        (newData.earnings || []).forEach((e: any) => earningsMap.set(e.month, e));
        
        const monthOrder: Record<string, number> = {
          'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
          'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
        };
        
        const sortedEarnings = Array.from(earningsMap.values()).sort((a: any, b: any) => {
          const [yearA, monthA] = a.month.split('-');
          const [yearB, monthB] = b.month.split('-');
          if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
          return monthOrder[monthA.toUpperCase()] - monthOrder[monthB.toUpperCase()];
        });
        
        return {
          ...prev,
          ...newData,
          earnings: sortedEarnings
        };
      });
    };

    try {
      let base64Images: string[] = [];
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isImage = file.type.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif)$/i.test(file.name);
      
      if (isPdf) {
        const arrayBuffer = await file.arrayBuffer();
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          throw new Error("The uploaded file is empty or could not be read.");
        }
        
        if (!pdfjsLib) {
          throw new Error("PDF Library not loaded. Please refresh the page and try again.");
        }

        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        loadingTask.onPassword = () => {
          throw new Error("This PDF is password protected. Please remove the password and try again.");
        };
        
        let pdf;
        try {
          pdf = await loadingTask.promise;
        } catch (pdfError: any) {
          console.error("PDF.js Load Error:", pdfError);
          throw new Error(`Failed to load PDF: ${pdfError.message || "Invalid PDF format"}`);
        }
        
        const images: string[] = [];
        const pagesToProcess = pdf.numPages >= 2 ? [2] : [1];
        
        for (const pageNum of pagesToProcess) {
          try {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 2.0 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            if (context) {
              await page.render({ canvasContext: context, viewport }).promise;
              images.push(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
            }
          } catch (pageError) {
            console.warn(`Failed to render page ${pageNum}:`, pageError);
          }
        }
        
        if (images.length === 0) {
          throw new Error("Could not render any pages from the PDF. It might be corrupted or in an unsupported format.");
        }
        base64Images = images;
      } else if (isImage) {
        const reader = new FileReader();
        const b64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        base64Images = [b64];
      } else {
        throw new Error("Unsupported file type. Please upload a PDF or an Image.");
      }

      let extractedData: any = {};
      let usedGemini = false;

      // Initialize extraction
      let apiKey = (import.meta.env.VITE_GEMINI_API_KEY as string);
      
      // Fallback for different environments
      if (!apiKey && typeof window !== 'undefined') {
        apiKey = (window as any).process?.env?.GEMINI_API_KEY || (window as any).VITE_GEMINI_API_KEY;
      }

      if (!apiKey && usedGemini) {
        throw new Error("API Key not found. Please set VITE_GEMINI_API_KEY in Environment Variables.");
      }

      // Targeted Text Extraction for Yearly Slip (Non-AI / Primary)
      if (file.type === 'application/pdf') {
        try {
          console.log("Starting Targeted PDF Text Extraction for Yearly Slip...");
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          
          // Target Page 2 - Bihar HRMS Yearly Slips have the detailed table on Page 2
          // We scan both but prioritize page 2 for the table as per user request
          let textLines = "";
          let page2Text = "";
          const pagesToScan = Math.min(2, pdf.numPages);
          for (let p = 1; p <= pagesToScan; p++) {
            const page = await pdf.getPage(p);
            const textContent = await page.getTextContent();
            
            // CRITICAL: Sort items by position (Y then X) to handle table data correctly
            const items = [...textContent.items] as any[];
            items.sort((a, b) => {
              const yDiff = b.transform[5] - a.transform[5];
              if (Math.abs(yDiff) < 5) { // Same line (with 5px tolerance)
                return a.transform[4] - b.transform[4]; // Sort by X
              }
              return yDiff; // Sort by Y (top to bottom)
            });
            
            const pageText = items.map((item: any) => item.str).join(" ") + " ";
            textLines += pageText;
            if (p === 2) page2Text = pageText;
          }

          // Use Page 2 text for table extraction if available, otherwise fallback to all text
          const tableSourceText = page2Text || textLines;

          // Personal Details extraction re-enabled for Name as per user request
          // Using a more robust regex to handle potential OCR variations
          const nameRegex = /((?:Employee\s*Name|Name))\s*[:\s-]*\s*([^:]+?)(?=\s*(?:Current\s*Office|Designation|GPF|$))/i;
          const nameMatch = textLines.match(nameRegex);
          if (nameMatch && nameMatch[2]) {
            const foundName = nameMatch[2].trim().replace(/^[:\s-]+/, '').replace(/\s+/g, ' ');
            const foundKeyword = nameMatch[1].trim();
            if (foundName && foundName.length > 2) {
              extractedData.employeeName = foundName;
              setExtractedName(foundName);
              setNameKeyword(foundKeyword);
              setExtractionMethod("Regular Expression (Pattern Match)");
              console.log("Regex Extracted Name:", foundName, "Keyword:", foundKeyword);
            }
          }

          // PRAN Extraction Logic - Extremely flexible to handle any OCR variations
          // Look for "PRAN" or "GPF" followed by any characters and then exactly 12 digits
          const pranRegex = /((?:GPF|PRAN)\s*(?:\/|No|Number)?)\s*[:\s-]*\s*(\d{12})/i;
          let pranMatch = (page2Text || "").match(pranRegex);
          
          // Fallback to all text if not found on page 2
          if (!pranMatch) {
            pranMatch = textLines.match(pranRegex);
          }

          if (pranMatch && pranMatch[2]) {
            const foundPran = pranMatch[2].trim();
            const foundKeyword = pranMatch[1].trim();
            extractedData.pranNo = foundPran;
            setExtractedPran(foundPran);
            setPranNo(foundPran); // Auto-fill the PRAN NO text box
            setPranExtractionMethod("Super-Flexible Search");
            setPranKeyword(foundKeyword);
            console.log("Regex Extracted PRAN:", foundPran, "Keyword:", foundKeyword);
          } else {
            // Last resort: Look for any 12-digit number in the text if PRAN word is nearby
            const broadMatch = textLines.match(/(PRAN)[\s\S]{1,50}(\d{12})/i);
            if (broadMatch && broadMatch[2]) {
              const foundPran = broadMatch[2].trim();
              const foundKeyword = broadMatch[1].trim();
              extractedData.pranNo = foundPran;
              setExtractedPran(foundPran);
              setPranNo(foundPran);
              setPranExtractionMethod("Broad Search (Last Resort)");
              setPranKeyword(foundKeyword);
              console.log("Broad Regex Extracted PRAN:", foundPran, "Keyword:", foundKeyword);
            }
          }

          // Designation, Class and Subject Extraction
          const desigRegex = /Designation(?:\s*\/\s*Class)?\s*[:\s-]*\s*([^:]+?)(?=\s*(?:Employee\s*Name|Name|Current\s*Office|$))/i;
          const desigMatch = textLines.match(desigRegex);
          if (desigMatch && desigMatch[1]) {
            const rawDesig = desigMatch[1].trim();
            console.log("Regex Extracted Designation Raw:", rawDesig);
            setDesignationExtractionMethod("Regex Match");
            setDesignationKeyword("Designation / Class");
            setPhaseExtractionMethod("Regex Match");
            setPhaseKeyword("Designation / Class");
            
            // Pattern: Designation (Class)
            // Example: "School Teacher(9-10)"
            const parts = rawDesig.match(/^([^(]+)(?:\(([^)]+)\))?/);
            if (parts) {
              const phasePart = parts[1]?.trim();
              const gradePart = parts[2]?.trim();

              // 1. Set Phase Suffix from first part of designation
              if (phasePart) {
                setPhaseSuffix(phasePart);
              }

              // 2. Fill Class (Grade Level)
              if (gradePart) {
                const matchedGrade = GRADE_LEVELS.find(g => gradePart.includes(g));
                if (matchedGrade) {
                  setSelectedGrade(matchedGrade);
                } else {
                  // If not in our list, just set it as is
                  setSelectedGrade(gradePart);
                }
              }

              setDesignation(rawDesig);
              console.log("Parsed Designation -> Phase Suffix:", phasePart, "Grade:", gradePart);
            }
          }
          
          // Robust Number Extraction
          const extractNumbers = (label: string, source: string) => {
            // Flexible regex for labels that might be split or have extra spaces
            const flexibleLabel = label.split(' ').join('\\s*');
            
            // Look for the label, then optional non-digit characters (like ":" or "Details"), 
            // then a sequence of numbers (digits, spaces, commas, dots)
            const regex = new RegExp(`(${flexibleLabel})[^\\d]*?([\\d\\s,.]+)`, 'i');
            const match = source.match(regex);
            if (!match) return { values: [], keyword: null };
            
            const matchedKeyword = match[1].trim();
            // Find all sequences that look like numbers (digits, possibly with commas/dots)
            const numberStrings = match[2].match(/\d[\d,.]*/g) || [];
            
            // Clean them up by removing commas and ensuring they are valid numbers
            const values = numberStrings
              .map(v => v.replace(/,/g, ''))
              .filter(v => v.length > 0 && !isNaN(parseFloat(v)));
            
            // If we have 13 values, the last one is likely the Total. Take first 12.
            if (values.length >= 13) return { values: values.slice(0, 12), keyword: matchedKeyword };
            return { values, keyword: matchedKeyword };
          };

          let basicRes = extractNumbers("Basic Pay", tableSourceText);
          if (basicRes.values.length === 0) basicRes = extractNumbers("PAY", tableSourceText);
          const basics = basicRes.values;
          
          let daRes = extractNumbers("Dearness", tableSourceText);
          if (daRes.values.length === 0) daRes = extractNumbers("Dearness Allowance", tableSourceText);
          if (daRes.values.length === 0) daRes = extractNumbers("D.A.", tableSourceText);
          if (daRes.values.length === 0) daRes = extractNumbers("DA", tableSourceText);
          const das = daRes.values;
          
          let hraRes = extractNumbers("House Rent Allowance", tableSourceText);
          if (hraRes.values.length === 0) hraRes = extractNumbers("House Rent", tableSourceText);
          if (hraRes.values.length === 0) hraRes = extractNumbers("H.R.A.", tableSourceText);
          if (hraRes.values.length === 0) hraRes = extractNumbers("HRA", tableSourceText);
          const hras = hraRes.values;

          // Store keywords for display
          setMonthlyKeywords({
            basic: basicRes.keyword || "NOT FOUND",
            da: daRes.keyword || "NOT FOUND",
            hra: hraRes.keyword || "NOT FOUND"
          });
          
          console.log("Extracted Basics:", basics);
          console.log("Extracted DAs:", das);
          console.log("Extracted HRAs:", hras);

          if (basics.length > 0) {
            // Map to months (assuming March to February order in the slip)
            const months = ["MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC", "JAN", "FEB"];
            const earnings: any[] = [];
            
            if (!extractedData.employeeName) {
              setExtractedName('NOT FOUND');
            }
            if (!extractedData.pranNo) {
              setExtractedPran('NOT FOUND');
            }
            
            const fyString = fy || "FY 2023-2024";
            const fyStart = parseInt(fyString.split(' ')[1]?.split('-')[0]) || 2023;

            const monthsToProcess = Math.min(months.length, basics.length);
            for (let i = 0; i < monthsToProcess; i++) {
              const year = (i < 10) ? fyStart : fyStart + 1;
              
              // Rounding Rules: Basic Pay to nearest 10, others to nearest integer
              const bVal = Math.round(parseFloat(basics[i] || '0') / 10) * 10;
              const dVal = Math.round(parseFloat(das[i] || '0'));
              const hVal = Math.round(parseFloat(hras[i] || '0'));

              earnings.push({
                month: `${year}-${months[i]}`,
                basic: bVal.toString(),
                da: dVal.toString(),
                hra: hVal.toString(),
                nps: Math.round((bVal + dVal) * 0.10).toString()
              });
            }

            extractedData.earnings = earnings;
            updateDebugDataState(extractedData);
            setExtractionMethod("Frontend Regex (Fast)");
            setMonthlyExtractionSource("Frontend Regex (Fast)");
            usedGemini = false;
            console.log("PDF Keyword Extraction Success:", extractedData.earnings.length, "months processed.");
          } else {
            console.log("No basic pay data found via frontend regex, trying Background Server...");
            
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fy", fy || "FY 2023-2024");

            const serverResponse = await fetch("/api/parse-pdf", {
              method: "POST",
              body: formData
            });

            if (serverResponse.ok) {
              const serverResult = await serverResponse.json();
              if (serverResult.success && serverResult.data.earnings.length > 0) {
                extractedData = serverResult.data;
                updateDebugDataState(extractedData);
                setExtractionMethod("Backend Server (Advanced)");
                setMonthlyExtractionSource("Backend Server (Advanced)");
                usedGemini = false;
                console.log("Background Server Extraction Success:", extractedData.earnings.length, "months processed.");
              } else {
                console.log("Background Server also failed to find data, falling back to Gemini...");
                usedGemini = true;
              }
            } else {
              console.log("Background Server error, falling back to Gemini...");
              usedGemini = true;
            }
          }
        } catch (err: any) {
          console.error("PDF Extraction Error:", err);
          usedGemini = true;
        }
      } else {
        usedGemini = true;
      }

      if (usedGemini) {
        // Initialize Gemini AI only if needed
        const apiKey = (import.meta.env.VITE_GEMINI_API_KEY as string) || (window as any).process?.env?.GEMINI_API_KEY;
        if (!apiKey) {
          throw new Error("API Key not found. Please set VITE_GEMINI_API_KEY in Hostinger Environment Variables.");
        }
        const ai = new GoogleGenAI({ apiKey });

        const contentParts = base64Images.map(img => ({
          inlineData: {
            mimeType: "image/jpeg",
            data: img
          }
        }));

        const promptText = `Extract data from this Bihar HRMS Yearly Salary Slip.
          Focus ONLY on the earnings table (usually on Page 2).
          Months are columns, components are rows.
          
          Extract:
          - employeeName
          - pranNo
          - designation
          - earnings: array of {month (YYYY-MMM), basic, da, hra, medical, nps}
          
          Return ONLY JSON.`;

        contentParts.push({ text: promptText } as any);

        let response;
        let retries = 3;
        let delay = 2000;

        while (retries > 0) {
          try {
            response = await ai.models.generateContent({
              model: "gemini-3-flash-preview",
              contents: [
                {
                  parts: contentParts
                }
              ],
              config: {
                thinkingConfig: { thinkingLevel: ThinkingLevel.LOW },
                maxOutputTokens: 1024,
                systemInstruction: "You are a precise data extractor for Bihar HRMS Salary Slips. Extract EXACT values. Focus on Page 2 table. Return ONLY JSON.",
                responseMimeType: "application/json",
                responseSchema: {
                  type: Type.OBJECT,
                  properties: {
                    employeeName: { type: Type.STRING },
                    pranNo: { type: Type.STRING },
                    designation: { type: Type.STRING },
                    earnings: {
                      type: Type.ARRAY,
                      items: {
                        type: Type.OBJECT,
                        properties: {
                          month: { type: Type.STRING },
                          basic: { type: Type.NUMBER },
                          da: { type: Type.NUMBER },
                          hra: { type: Type.NUMBER },
                          medical: { type: Type.NUMBER },
                          nps: { type: Type.NUMBER }
                        },
                        required: ["month", "basic", "da", "hra", "nps"]
                      }
                    }
                  },
                  required: ["earnings", "employeeName", "pranNo", "designation"]
                }
              }
            });
            break; // Success!
          } catch (err: any) {
            if (err.message?.includes("503") || err.message?.includes("high demand") || err.message?.includes("UNAVAILABLE")) {
              retries--;
              if (retries === 0) throw err;
              console.log(`Gemini busy, retrying in ${delay}ms... (${retries} retries left)`);
              await new Promise(res => setTimeout(res, delay));
              delay *= 2; // Exponential backoff
            } else {
              throw err;
            }
          }
        }

        if (!response) throw new Error("Failed to get response from AI model.");
        const geminiResult = JSON.parse(response.text || "{}");
        
        // Apply Rounding Rules to Gemini Result
        if (geminiResult.earnings) {
          geminiResult.earnings = geminiResult.earnings.map((e: any) => ({
            ...e,
            basic: Math.round((parseFloat(e.basic) || 0) / 10) * 10,
            da: Math.round(parseFloat(e.da) || 0),
            hra: Math.round(parseFloat(e.hra) || 0),
            nps: Math.round(parseFloat(e.nps) || 0)
          }));
        }

        if (geminiResult.employeeName) {
          const gName = geminiResult.employeeName.trim().toUpperCase();
          if (gName && gName.length > 2) {
            extractedData.employeeName = gName;
            setExtractedName(gName);
            setNameKeyword("AI Analysis");
            console.log("Gemini Extracted Name:", gName);
          }
        }

        if (geminiResult.pranNo) {
          const gPran = geminiResult.pranNo.trim();
          if (gPran && gPran.length === 12) {
            extractedData.pranNo = gPran;
            setExtractedPran(gPran);
            setPranNo(gPran);
            console.log("Gemini Extracted PRAN:", gPran);
          }
        }

        if (geminiResult.designation) {
          const gDesig = geminiResult.designation.trim();
          setDesignation(gDesig);
          setDesignationExtractionMethod("Google Gemini AI (Smart Vision)");
          setDesignationKeyword("AI Analysis");
          setPhaseExtractionMethod("Google Gemini AI (Smart Vision)");
          setPhaseKeyword("AI Analysis");
          
          // Apply same splitting and appending logic for Gemini result
          const parts = gDesig.match(/^([^(]+)(?:\(([^)]+)\))?/);
          if (parts) {
            const phasePart = parts[1]?.trim();
            const gradePart = parts[2]?.trim();

            if (phasePart) {
              setPhaseSuffix(phasePart);
            }
            if (gradePart) {
              const matchedGrade = GRADE_LEVELS.find(g => gradePart.includes(g));
              if (matchedGrade) {
                setSelectedGrade(matchedGrade);
              } else {
                setSelectedGrade(gradePart);
              }
            }
          }
        }
        
        extractedData = { ...extractedData, ...geminiResult };
        updateDebugDataState(extractedData);
        setExtractionMethod("Google Gemini AI (Smart Vision)");
        setMonthlyExtractionSource("Google Gemini AI (Smart Vision)");
      }
      
      // Monthly Data Processing Logic follows below
      const monthMap: Record<string, string> = {
        'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04', 'MAY': '05', 'JUN': '06',
        'JUL': '07', 'AUG': '08', 'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
      };

      const formattedExtracted: Record<string, { basic: string, da: string, hra: string, nps: string }> = {};
      (extractedData.earnings || []).forEach((item: any) => {
        const match = item.month.match(/(\d{4})-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i);
        if (match) {
          const monthKey = `${match[1]}-${monthMap[match[2].toUpperCase()]}`;
          formattedExtracted[monthKey] = {
            basic: item.basic.toString().replace(/,/g, ''),
            da: item.da.toString().replace(/,/g, ''),
            hra: item.hra.toString().replace(/,/g, ''),
            nps: item.nps ? item.nps.toString().replace(/,/g, '') : '0'
          };
        }
      });

      if (Object.keys(formattedExtracted).length > 0) {
        setMonthlyDrawn(prevDrawn => {
          const updatedDrawn = prevDrawn.map(m => {
            if (formattedExtracted[m.month]) {
              const data = formattedExtracted[m.month];
              const basicNum = Math.round((parseFloat(data.basic) || 0) / 10) * 10;
              const daNum = Math.round(parseFloat(data.da) || 0);
              const hraNum = Math.round(parseFloat(data.hra) || 0);
              
              const daPercent = basicNum > 0 ? Math.round((daNum / basicNum) * 100).toString() : m.daPercent;
              const hraPercent = basicNum > 0 ? (Math.round((hraNum / basicNum) * 100 * 10) / 10).toString() : m.hraPercent;

              return {
                ...m,
                basic: basicNum.toString(),
                da: daNum.toString(),
                daPercent,
                hra: hraNum.toString(),
                hraPercent,
                medical: m.medical || '1000',
                nps: data.nps ? Math.round(parseFloat(data.nps)).toString() : Math.round((basicNum + daNum) * 0.10).toString()
              };
            }
            return m;
          });

          // If a month has a lower basic than the previous month's drawn basic,
          // set the claimed (Due) basic equal to the drawn basic for that month.
          setMonthlyDue(prevDue => prevDue.map((m, idx) => {
            if (idx > 0 && updatedDrawn[idx] && updatedDrawn[idx-1]) {
              const currentDrawnBasic = parseFloat(updatedDrawn[idx].basic) || 0;
              const prevDrawnBasic = parseFloat(updatedDrawn[idx-1].basic) || 0;
              
              if (currentDrawnBasic < prevDrawnBasic && currentDrawnBasic > 0 && prevDrawnBasic > 0) {
                const basicNum = currentDrawnBasic;
                const daRate = parseFloat(m.daPercent) || 0;
                const hraRate = parseFloat(m.hraPercent) || 0;
                
                const daNum = Math.round((basicNum * daRate) / 100);
                const hraNum = Math.round((basicNum * hraRate) / 100);
                const npsNum = Math.round((basicNum + daNum) * 0.10);

                return { 
                  ...m, 
                  basic: updatedDrawn[idx].basic,
                  da: daNum.toString(),
                  hra: hraNum.toString(),
                  nps: npsNum.toString()
                };
              }
            }
            return m;
          }));

          return updatedDrawn;
        });
        
        // Success message hidden as per user request, replaced with simple 'UPLOADED'
        alert("UPLOADED");
      } else {
        alert("⚠️ No matching data found. Please ensure the 'Earning Details' section is clear on page 2.");
      }

    } catch (error: any) {
      console.error("Processing Error:", error);
      let errorMsg = error.message || "Could not extract data from PDF";
      
      if (errorMsg.includes("503") || errorMsg.includes("high demand") || errorMsg.includes("UNAVAILABLE")) {
        errorMsg = "Google AI Server is currently busy (High Demand). Please wait 10-20 seconds and try again. This is a temporary issue.";
      }
      
      alert(`❌ Processing Error: ${errorMsg}. Please try again or enter data manually.`);
    } finally {
      if (fy) setParsingYears(prev => prev.filter(y => y !== fy));
    }
  };

  // Automatic parsing triggered on file upload
  
  const getAutoBasicValue = (phaseInput: string, gradeInput: string, dateKey: string) => {
    const phase = phaseInput.toUpperCase();
    const grade = gradeInput.toUpperCase();

    if (phase.startsWith("HEADMASTER")) return "35000";
    if (phase.startsWith("HEAD TEACHER")) return "31500";
    if (phase.startsWith("EXCLUSIVE") || phase.startsWith("ASSISTANT TEACHER(NIYOJIT)")) return "";

    const [year, month] = dateKey.split('-').map(Number);
    const dateNum = year * 100 + month;

    // Smart match for Phase
    const isTRE1 = phase.startsWith("BPSC TRE-1") && !phase.includes("SUPPLIMENTRY");
    const isTRE2 = phase.startsWith("BPSC TRE-2") || phase.includes("SUPPLIMENTRY");
    const isTRE3 = phase.startsWith("BPSC TRE-3");

    // Smart match for Grade
    const is1to5 = grade.includes("1-5");
    const is6to8 = grade.includes("6-8");
    const is9to10 = grade.includes("9-10");
    const is11to12 = grade.includes("11-12");

    if (isTRE1) {
      if (is1to5) {
        if (dateNum <= 202406) return "25000";
        if (dateNum <= 202506) return "25750";
        return "26520";
      }
      if (is6to8) {
        if (dateNum <= 202406) return "28000";
        if (dateNum <= 202506) return "28840";
        return "29710";
      }
      if (is9to10) {
        if (dateNum <= 202406) return "31000";
        if (dateNum <= 202506) return "31930";
        return "32890";
      }
      if (is11to12) {
        if (dateNum <= 202406) return "32000";
        if (dateNum <= 202506) return "32960";
        return "33950";
      }
    }

    if (isTRE2) {
      if (is1to5) {
        if (dateNum <= 202412) return "25000";
        if (dateNum <= 202512) return "25750";
        return "26520";
      }
      if (is6to8) {
        if (dateNum <= 202412) return "28000";
        if (dateNum <= 202512) return "28840";
        return "29710";
      }
      if (is9to10) {
        if (dateNum <= 202412) return "31000";
        if (dateNum <= 202512) return "31930";
        return "32890";
      }
      if (is11to12) {
        if (dateNum <= 202412) return "32000";
        if (dateNum <= 202512) return "32960";
        return "33950";
      }
    }

    if (isTRE3) {
      if (is1to5) {
        if (dateNum <= 202512) return "25000";
        return "25750";
      }
      if (is6to8) {
        if (dateNum <= 202512) return "28000";
        return "28840";
      }
      if (is9to10) {
        if (dateNum <= 202512) return "31000";
        return "31930";
      }
      if (is11to12) {
        if (dateNum <= 202512) return "32000";
        return "32960";
      }
    }

    return "";
  };

  const getDARateForMonth = (dateStr: string) => {
    const found = DA_RATES.find(d => d.month === dateStr);
    return found ? found.rate : 60; 
  };

  useEffect(() => {
    const start = new Date(startDate + "-01");
    const end = new Date(endDate + "-01");
    if (end < start) return;

    const generateMonthlyData = (existingData: MonthlyData[], isDue: boolean) => {
      const newData: MonthlyData[] = [];
      let current = new Date(start);
      while (current <= end) {
        const dateKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = current.toLocaleString('default', { month: 'short', year: 'numeric' });
        
        const existing = existingData.find(d => d.month === dateKey);
        
        if (existing) {
          if (claimedPayMode === 'auto' && (isDue || !debugData?.earnings)) {
            const autoBasic = getAutoBasicValue(selectedPhase, selectedGrade, dateKey);
            const daRate = getDARateForMonth(dateKey);
            const basicNum = parseFloat(autoBasic) || 0;
            const daNum = Math.round((basicNum * daRate) / 100);
            
            let hraPercent = existing.hraPercent;
            if (isDue && hraIncreaseMonth && hraIncreasePercent) {
              if (dateKey >= hraIncreaseMonth) {
                hraPercent = hraIncreasePercent.toString();
              }
            }

            const hraNum = Math.round((basicNum * parseFloat(hraPercent)) / 100);
            const npsNum = Math.round((basicNum + daNum) * 0.10);

            newData.push({
              ...existing,
              basic: autoBasic,
              da: daNum.toString(),
              daPercent: daRate.toString(),
              hraPercent: hraPercent,
              hra: hraNum.toString(),
              nps: npsNum.toString(),
              incrementApplicable: false
            });
          } else {
            newData.push({ ...existing, incrementApplicable: existing.incrementApplicable ?? false });
          }
        } else {
          const daRate = getDARateForMonth(dateKey);
          const [y, m_] = dateKey.split('-').map(Number);
          const dNum = y * 100 + m_;
          
          let defaultBasic = '0';
          if (isDue) {
            defaultBasic = claimedPayMode === 'auto' ? getAutoBasicValue(selectedPhase, selectedGrade, dateKey) : '26500';
          } else {
            if (claimedPayMode === 'auto' && !debugData?.earnings) {
              defaultBasic = getAutoBasicValue(selectedPhase, selectedGrade, dateKey);
            } else {
              defaultBasic = isAutomatic ? '0' : '25000';
            }
          }
          
          // HRA Logic: From Jan 2024, rates are 20%, 10%, 7.5%, 5%. Default to 5% for Due after Jan 2024.
          let defaultHra = '4';
          if (isDue) {
            defaultHra = dNum >= 202401 ? '5' : '4';
          } else {
            defaultHra = '4';
          }
          
          let basicNum = parseFloat(defaultBasic) || 0;
          let daPercent = daRate.toString();
          let hraPercent = defaultHra;

          if (isDue && hraIncreaseMonth && hraIncreasePercent) {
            if (dateKey >= hraIncreaseMonth) {
              hraPercent = hraIncreasePercent.toString();
            }
          }
          
          let daNum = isDue || !isAutomatic ? Math.round((basicNum * daRate) / 100) : 0;
          let hraNum = isDue || !isAutomatic ? Math.round((basicNum * parseFloat(hraPercent)) / 100) : 0;
          let npsNum = isDue || !isAutomatic ? Math.round((basicNum + daNum) * 0.10) : 0;
          
          let medical = '0';
          if (basicNum > 0) {
            if (joiningDate) {
              try {
                const jDate = new Date(joiningDate);
                if (!isNaN(jDate.getTime())) {
                  const jMonth = jDate.getMonth();
                  const jYear = jDate.getFullYear();
                  const jDay = jDate.getDate();
                  
                  const [cYear, cMonth] = dateKey.split('-').map(Number);
                  const currentMonthIndex = cMonth - 1;
                  const currentYear = cYear;

                  if (currentYear === jYear && currentMonthIndex === jMonth) {
                    const daysInMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate();
                    const daysWorked = daysInMonth - jDay + 1;
                    medical = Math.round((1000 / daysInMonth) * daysWorked).toString();
                  } else if (currentYear > jYear || (currentYear === jYear && currentMonthIndex > jMonth)) {
                    medical = '1000';
                  } else {
                    medical = '0';
                  }
                } else {
                  medical = isDue || !isAutomatic ? '1000' : '0';
                }
              } catch (e) {
                medical = isDue || !isAutomatic ? '1000' : '0';
              }
            } else {
              medical = isDue || !isAutomatic ? '1000' : '0';
            }
          } else {
            medical = '0';
          }

          if (!isDue && debugData?.earnings) {
            const monthMap: Record<string, string> = {
              'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04', 'MAY': '05', 'JUN': '06',
              'JUL': '07', 'AUG': '08', 'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
            };
            const extractedMonth = debugData.earnings.find((e: any) => {
              const match = e.month.match(/(\d{4})-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i);
              if (match) {
                return `${match[1]}-${monthMap[match[2].toUpperCase()]}` === dateKey;
              }
              return false;
            });

            if (extractedMonth) {
              defaultBasic = extractedMonth.basic.toString().replace(/,/g, '');
              basicNum = parseFloat(defaultBasic) || 0;
              
              const extDa = extractedMonth.da.toString().replace(/,/g, '');
              daNum = Math.round(parseFloat(extDa) || 0);
              daPercent = basicNum > 0 ? Math.round((daNum / basicNum) * 100).toString() : daPercent;
              
              const extHra = extractedMonth.hra.toString().replace(/,/g, '');
              hraNum = Math.round(parseFloat(extHra) || 0);
              hraPercent = basicNum > 0 ? (Math.round((hraNum / basicNum) * 100 * 10) / 10).toString() : hraPercent;
              
              if (extractedMonth.medical) {
                medical = Math.round(parseFloat(extractedMonth.medical.toString().replace(/,/g, '')) || 0).toString();
              }
              
              if (extractedMonth.nps) {
                npsNum = Math.round(parseFloat(extractedMonth.nps.toString().replace(/,/g, '')) || 0);
              } else {
                npsNum = Math.round((basicNum + daNum) * 0.10);
              }
            }
          }

          newData.push({
            month: dateKey,
            monthLabel: monthLabel,
            basic: defaultBasic,
            daPercent: daPercent,
            da: daNum.toString(),
            hraPercent: hraPercent,
            hra: hraNum.toString(),
            medical: medical,
            nps: npsNum.toString(),
            gic: isDue || !isAutomatic ? '30' : '0',
            tds: '0',
            isExpanded: newData.length === 0,
            incrementApplicable: false
          });
        }
        current.setMonth(current.getMonth() + 1);
      }
      return newData;
    };

    setMonthlyDue(prev => generateMonthlyData(prev, true));
    setMonthlyDrawn(prev => generateMonthlyData(prev, false));
  }, [startDate, endDate, claimedPayMode, selectedPhase, selectedGrade, debugData, joiningDate, hraIncreaseMonth, hraIncreasePercent]);

  const updateMonth = (type: 'due' | 'drawn', index: number, field: keyof MonthlyData, value: any) => {
    const state = type === 'due' ? monthlyDue : monthlyDrawn;
    const setState = type === 'due' ? setMonthlyDue : setMonthlyDrawn;
    
    const updated = [...state];
    let finalValue = value;
    
    // Round DA, HRA, NPS if manually entered
    if (field === 'da' || field === 'hra' || field === 'nps' || field === 'medical' || field === 'gic' || field === 'tds') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        finalValue = Math.round(num).toString();
      }
    }

    updated[index] = { ...updated[index], [field]: finalValue };

    if (field === 'basic' || field === 'daPercent' || field === 'hraPercent') {
      const b = parseFloat(updated[index].basic) || 0;
      const dp = parseFloat(updated[index].daPercent) || 0;
      const hp = parseFloat(updated[index].hraPercent) || 0;
      
      updated[index].da = Math.round((b * dp) / 100).toString();
      updated[index].hra = Math.round((b * hp) / 100).toString();
      updated[index].nps = Math.round((b + (parseFloat(updated[index].da) || 0)) * 0.10).toString();

      // Update Medical Allowance based on the rule
      if (b === 0) {
        updated[index].medical = '0';
      } else {
        // Re-apply joining date rule
        if (joiningDate) {
          try {
            const jDate = new Date(joiningDate);
            if (!isNaN(jDate.getTime())) {
              const jMonth = jDate.getMonth();
              const jYear = jDate.getFullYear();
              const jDay = jDate.getDate();
              
              const [cYear, cMonth] = updated[index].month.split('-').map(Number);
              const currentMonthIndex = cMonth - 1;
              const currentYear = cYear;

              if (currentYear === jYear && currentMonthIndex === jMonth) {
                const daysInMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate();
                const daysWorked = daysInMonth - jDay + 1;
                updated[index].medical = Math.round((1000 / daysInMonth) * daysWorked).toString();
              } else if (currentYear > jYear || (currentYear === jYear && currentMonthIndex > jMonth)) {
                updated[index].medical = '1000';
              } else {
                updated[index].medical = '0';
              }
            } else {
              updated[index].medical = '1000';
            }
          } catch (e) {
            updated[index].medical = '1000';
          }
        } else {
          updated[index].medical = '1000';
        }
      }

      // If HRA percent changes, apply to all future months
      if (field === 'hraPercent') {
        for (let i = index + 1; i < updated.length; i++) {
          updated[i].hraPercent = finalValue;
          const futureB = parseFloat(updated[i].basic) || 0;
          updated[i].hra = Math.round((futureB * hp) / 100).toString();
        }
      }
    } else if (field === 'da') {
      const b = parseFloat(updated[index].basic) || 0;
      updated[index].nps = Math.round((b + (parseFloat(updated[index].da) || 0)) * 0.10).toString();
    } else if (field === 'hra') {
      const b = parseFloat(updated[index].basic) || 0;
      const h = parseFloat(updated[index].hra) || 0;
      let newHraPercent = '';
      if (b > 0) {
        newHraPercent = ((h / b) * 100).toFixed(2).replace(/\.?0+$/, '');
        updated[index].hraPercent = newHraPercent;
      }
      
      // If HRA amount changes, apply the new percent to all future months
      for (let i = index + 1; i < updated.length; i++) {
        if (newHraPercent) {
          updated[i].hraPercent = newHraPercent;
          const futureB = parseFloat(updated[i].basic) || 0;
          updated[i].hra = Math.round((futureB * parseFloat(newHraPercent)) / 100).toString();
        } else {
          updated[i].hra = finalValue;
        }
      }
    }

    setState(updated);
  };

  const copyToNextMonth = (type: 'due' | 'drawn', index: number) => {
    const state = type === 'due' ? monthlyDue : monthlyDrawn;
    const setState = type === 'due' ? setMonthlyDue : setMonthlyDrawn;
    
    if (index < state.length - 1) {
      const updated = [...state];
      const source = updated[index];
      
      updated[index + 1] = {
        ...updated[index + 1],
        basic: source.basic,
        daPercent: source.daPercent,
        da: source.da,
        hraPercent: source.hraPercent,
        hra: source.hra,
        nps: source.nps,
        medical: source.medical,
        gic: source.gic,
        tds: source.tds,
        isExpanded: true
      };
      
      setState(updated);
    }
  };

  const copyToAllRemaining = (type: 'due' | 'drawn', index: number) => {
    const state = type === 'due' ? monthlyDue : monthlyDrawn;
    const setState = type === 'due' ? setMonthlyDue : setMonthlyDrawn;
    
    if (index < state.length - 1) {
        const updated = [...state];
        const source = updated[index];
        
        for (let i = index + 1; i < updated.length; i++) {
            updated[i] = {
                ...updated[i],
                basic: source.basic,
                daPercent: source.daPercent,
                da: source.da,
                hraPercent: source.hraPercent,
                hra: source.hra,
                nps: source.nps,
                medical: source.medical,
                gic: source.gic,
                tds: source.tds,
                isExpanded: true
            };
        }
        setState(updated);
    }
  };

  const calculateNetPay = (m: MonthlyData) => {
    const earnings = (parseFloat(m.basic) || 0) + (parseFloat(m.da) || 0) + (parseFloat(m.hra) || 0) + (parseFloat(m.medical) || 0);
    const deductions = (parseFloat(m.nps) || 0) + (parseFloat(m.gic) || 0) + (parseFloat(m.tds) || 0);
    return earnings - deductions;
  };

  const validateUdise = async (code: string) => {
    if (!code || code.length < 11) {
      setUdiseStatus('none');
      return;
    }
    
    setUdiseStatus('searching');
    try {
      const apiKey = (import.meta.env.VITE_GEMINI_API_KEY as string) || (window as any).process?.env?.GEMINI_API_KEY;
      if (!apiKey) {
        throw new Error("API Key not found");
      }
      const ai = new GoogleGenAI({ apiKey });
      
      let response;
      let retries = 3;
      let delay = 2000;

      while (retries > 0) {
        try {
          response = await ai.models.generateContent({
            model: "gemini-3-flash-preview",
            contents: `Find the school name for Bihar UDISE Code: ${code}. If found, return ONLY the school name. If not found or invalid code, return "NOT_FOUND".`,
            config: {
              tools: [{ googleSearch: {} }]
            }
          });
          break;
        } catch (err: any) {
          if (err.message?.includes("503") || err.message?.includes("high demand") || err.message?.includes("UNAVAILABLE")) {
            retries--;
            if (retries === 0) throw err;
            await new Promise(res => setTimeout(res, delay));
            delay *= 2;
          } else {
            throw err;
          }
        }
      }

      if (!response) throw new Error("Failed to get response");
      const result = response.text?.trim() || "";
      if (result && result !== "NOT_FOUND") {
        setSchoolName(result);
        setUdiseStatus('valid');
      } else {
        setUdiseStatus('invalid');
      }
    } catch (error) {
      console.error("UDISE validation error:", error);
      setUdiseStatus('invalid');
    }
  };

  const handlePaymentAndGenerate = async (e: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();

    if (parsingYears.length > 0) {
      alert("⏳ Please wait! System is still processing your salary slips. Once 'READY' appears for all files, you can generate the PDF.");
      return;
    }
    
    if (!isExperiment && udiseCode && udiseStatus === 'invalid') {
      alert("⚠️ Invalid UDISE Code. Please enter a correct 11-digit Bihar UDISE Code or clear the field to proceed.");
      return;
    }

    try {
      setIsPaymentProcessing(true);

      // 1. Create order
      const orderResponse = await fetch('/api/create-razorpay-order', { method: 'POST' });
      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error('Failed to create payment order');
      }

      // 2. Configure Razorpay
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID || "rzp_live_SU7rTwBBstciAN",
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: "BR Teacher Portal",
        description: "Arrear PDF Download",
        order_id: orderData.order.id,
        handler: async function (response: any) {
          try {
            // 3. Verify payment
            const verifyRes = await fetch('/api/verify-razorpay-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(response)
            });
            const verifyData = await verifyRes.json();
            
            if (verifyData.success) {
              // 4. Generate PDF
              await handleGenerate(e);
            } else {
              alert("Payment verification failed!");
            }
          } catch (err) {
            console.error("Verification error:", err);
            alert("Payment verification error.");
          } finally {
            setIsPaymentProcessing(false);
          }
        },
        modal: {
          ondismiss: function() {
            setIsPaymentProcessing(false);
          }
        },
        theme: { color: "#ea580c" }
      };

      // 5. Open Razorpay popup
      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', function (response: any) {
        console.error("Payment failed:", response.error);
        alert("Payment failed: " + response.error.description);
        setIsPaymentProcessing(false);
      });
      rzp.open();

    } catch (err: any) {
      console.error("Payment error:", err);
      alert(err.message || "Something went wrong with the payment gateway.");
      setIsPaymentProcessing(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();

    if (parsingYears.length > 0) {
      alert("⏳ Please wait! System is still processing your salary slips. Once 'READY' appears for all files, you can generate the PDF.");
      return;
    }
    
    if (!isExperiment && udiseCode && udiseStatus === 'invalid') {
      alert("⚠️ Invalid UDISE Code. Please enter a correct 11-digit Bihar UDISE Code or clear the field to proceed.");
      return;
    }

    setLoading(true);
    try {
      const { jsPDF } = (window as any).jspdf;
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Helper to draw borders
      const drawBorder = (x: number, y: number, w: number, h: number) => doc.rect(x, y, w, h);
      const drawLine = (x1: number, y1: number, x2: number, y2: number) => doc.line(x1, y1, x2, y2);

      // Header Section
      const startY = 10;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      
      // Main Header Box
      doc.setLineWidth(0.3);
      doc.setDrawColor(0);
      doc.setFillColor(255, 255, 255);
      
      // Title Row
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      const title = `BLOCK EDUCATION OFFICE-${blockName.toUpperCase() || 'GOPALGANJ'}`;
      doc.text(title, pageWidth / 2, startY + 8, { align: "center" });
      drawBorder(margin, startY, contentWidth, 12);

      // Info Rows
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      
      const rowHeight = 7;
      let currentY = startY + 12;

      // Row 3: Phase | Class
      let rawClass = selectedGrade.toUpperCase();
      let phaseText = selectedPhase.toUpperCase();
      
      if (phaseSuffix) {
        // Avoid double brackets if already present in suffix
        const cleanSuffix = phaseSuffix.replace(/[()]/g, '').trim();
        if (cleanSuffix) {
          phaseText = `${selectedPhase.toUpperCase()} (${cleanSuffix.toUpperCase()})`;
        }
      }

      let extractedNameFromClass = '';

      // Final name should prefer extracted name if tName is empty
      const finalName = (extractedName || tName || extractedNameFromClass).toUpperCase();

      // Row 1: Name | School
      drawBorder(margin, currentY, contentWidth, rowHeight);
      doc.text(`NAME :- ${finalName}`, margin + 2, currentY + 5);
      drawLine(pageWidth / 2, currentY, pageWidth / 2, currentY + rowHeight); // Vertical separator
      doc.text(`SCHOOL NAME :- ${schoolName.toUpperCase()}`, (pageWidth / 2) + 2, currentY + 5);
      
      currentY += rowHeight;

      // Row 2: PRAN | Joining Date
      drawBorder(margin, currentY, contentWidth, rowHeight);
      doc.text(`PRAN NO :- ${pranNo}`, margin + 2, currentY + 5);
      drawLine(pageWidth / 2, currentY, pageWidth / 2, currentY + rowHeight);
      
      const formatJoiningDate = (dateStr: string) => {
        if (!dateStr) return '';
        const [y, m, d] = dateStr.split('-');
        return `${d}-${m}-${y}`;
      };
      doc.text(`JOINING DATE :- ${formatJoiningDate(joiningDate)}`, (pageWidth / 2) + 2, currentY + 5);

      currentY += rowHeight;

      drawBorder(margin, currentY, contentWidth, rowHeight);
      doc.text(`PHASE :- ${phaseText}`, margin + 2, currentY + 5);
      drawLine(pageWidth / 2, currentY, pageWidth / 2, currentY + rowHeight);
      
      const classText = `CLASS :- ${rawClass}${subject ? ` (${subject.toUpperCase()})` : ''}`;
      doc.text(classText, (pageWidth / 2) + 2, currentY + 5);

      currentY += rowHeight;

      // Table Data Preparation
      let totalDueBasic = 0, totalDueDA = 0, totalDueHRA = 0, totalDueTotal = 0;
      let totalPaidBasic = 0, totalPaidDA = 0, totalPaidHRA = 0, totalPaidTotal = 0;
      let totalDiffPay = 0, totalDiffDA = 0, totalDiffHRA = 0;
      let totalGrossAmount = 0, totalNPSDiff = 0, totalNetPay = 0;

      let currentHraPercentageOverride = 0;

      const tableRows = monthlyDue.map((mDue, idx) => {
        const mDrawn = monthlyDrawn[idx];
        
        // Rounding Rules: Basic Pay to nearest 10, others to nearest integer
        let dBasic = Math.round((parseFloat(mDue.basic) || 0) / 10) * 10;
        let dDA = Math.round(parseFloat(mDue.da) || 0);
        let dHra = Math.round(parseFloat(mDue.hra) || 0);
        let dNps = Math.round(parseFloat(mDue.nps) || 0);

        const pBasic = Math.round((parseFloat(mDrawn.basic) || 0) / 10) * 10;
        const pDA = Math.round(parseFloat(mDrawn.da) || 0);
        const pHra = Math.round(parseFloat(mDrawn.hra) || 0);
        const pTotal = pBasic + pDA + pHra;
        const pNps = Math.round(parseFloat(mDrawn.nps) || 0);

        // Joining Month Logic
        if (joiningDate && mDue.month === joiningDate.substring(0, 7)) {
          dBasic = pBasic; // CLAIMED AMOUNT PAY = DRAWN AMOUNT PAY
          const daPercent = parseFloat(mDue.daPercent) || 0;
          const hraPercent = parseFloat(mDue.hraPercent) || 0;
          dDA = Math.round(dBasic * (daPercent / 100));
          dHra = Math.round(dBasic * (hraPercent / 100));
        }

        // HRA Percentage Override Logic
        const drawnHraPercent = pBasic > 0 ? (pHra / pBasic) * 100 : 0;
        const originalClaimedHraPercent = dBasic > 0 ? (dHra / dBasic) * 100 : 0;

        // Calculate what the claimed percentage SHOULD be for this month based on drawn
        let targetHraPercent = drawnHraPercent;
        if (mDue.month >= "2024-01") {
          const dHP = Math.round(drawnHraPercent * 10) / 10;
          if (dHP >= 3.5 && dHP <= 4.5) targetHraPercent = 5;
          else if (dHP >= 5.5 && dHP <= 6.5) targetHraPercent = 7.5;
          else if (dHP >= 7.5 && dHP <= 8.5) targetHraPercent = 10;
          else if (dHP >= 15.5 && dHP <= 16.5) targetHraPercent = 20;
          
          // Minimum 5% rule starting Jan 2024
          if (targetHraPercent < 5 && drawnHraPercent > 0) targetHraPercent = 5;
        }

        if (targetHraPercent > originalClaimedHraPercent) {
          currentHraPercentageOverride = targetHraPercent;
        } else if (originalClaimedHraPercent > targetHraPercent && originalClaimedHraPercent > currentHraPercentageOverride) {
          currentHraPercentageOverride = 0; // Reset override as per rule
        }

        let effectiveHraPercent = Math.max(originalClaimedHraPercent, currentHraPercentageOverride);
        
        // Ensure minimum 5% starting Jan 2024 even if no drawn data or override
        if (mDue.month >= "2024-01" && effectiveHraPercent < 5) {
          effectiveHraPercent = 5;
        }

        if (effectiveHraPercent > originalClaimedHraPercent && dBasic > 0) {
          dHra = Math.round(dBasic * (effectiveHraPercent / 100));
        }

        const dTotal = dBasic + dDA + dHra;

        const diffPay = dBasic - pBasic;
        const diffDA = dDA - pDA;
        const diffHRA = dHra - pHra;
        const grossAmount = diffPay + diffDA + diffHRA;
        let npsDiff = Math.round((diffPay + diffDA) * 0.10);
        
        // Joining Month Logic for NPS
        if (joiningDate && mDue.month === joiningDate.substring(0, 7)) {
          npsDiff = 0; // NPS DEDUCTION '0' for joining month
        }

        const netPay = grossAmount - npsDiff;

        // Accumulate Totals
        totalDueBasic += dBasic; totalDueDA += dDA; totalDueHRA += dHra; totalDueTotal += dTotal;
        totalPaidBasic += pBasic; totalPaidDA += pDA; totalPaidHRA += pHra; totalPaidTotal += pTotal;
        totalDiffPay += diffPay; totalDiffDA += diffDA; totalDiffHRA += diffHRA;
        totalGrossAmount += grossAmount; totalNPSDiff += npsDiff; totalNetPay += netPay;

        return [
          mDue.monthLabel,
          dBasic, dDA, dHra, dTotal,
          pBasic, pDA, pHra, pTotal,
          diffPay, diffDA, diffHRA, grossAmount, npsDiff, netPay, ''
        ];
      });

      // Save usage data
      saveUsageData({
        section: isAutomatic ? 'Arear Automatic' : 'Manual Arrear',
        name: tName,
        schoolName: schoolName,
        udiseCode: udiseCode,
        className: designation || selectedGrade,
        panNumber: panNo,
        pranNumber: pranNo,
        mobileNumber: mobileNo,
        period: `${startDate} to ${endDate}`,
        amount: totalNetPay.toFixed(2)
      }).catch(console.error);

      // Total Row
      tableRows.push([
        'Total',
        totalDueBasic, totalDueDA, totalDueHRA, totalDueTotal,
        totalPaidBasic, totalPaidDA, totalPaidHRA, totalPaidTotal,
        totalDiffPay, totalDiffDA, totalDiffHRA, totalGrossAmount, totalNPSDiff, totalNetPay, ''
      ]);

      // Footer Logic
      const footerHeight = 40; // Approx height needed for footer
      const pageHeight = doc.internal.pageSize.height;
      
      // Dry Run to check final Y
      const dryDoc = new jsPDF('l', 'mm', 'a4');
      (dryDoc as any).autoTable({
        startY: currentY + 2,
        head: [
          [
            { content: 'Month', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } },
            { content: 'Claimed Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
            { content: 'Drawn Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
            { content: 'Difference Payable', colSpan: 6, styles: { halign: 'center', fillColor: [255, 228, 225] } },
            { content: 'Remarks', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } }
          ],
          [
            'PAY', 'D.A.', 'H.R.A', 'TOTAL',
            'PAY', 'D.A', 'H.R.A.', 'TOTAL',
            'PAY', 'D.A', 'H.R.A.', 'GROSS\nAMOUNT', 'NPS/PRAN\nDeduction', 'NET PAY\nAMOUNT'
          ],
          ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16']
        ],
        body: tableRows,
        theme: 'grid',
        styles: { fontSize: 10, fontStyle: 'bold', cellPadding: 1.5, lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0], halign: 'center' },
      });
      
      const dryFinalY = (dryDoc as any).lastAutoTable.finalY;
      const spaceLeft = pageHeight - dryFinalY;
      
      let finalY = 0;

      if (spaceLeft < footerHeight && tableRows.length > 3) {
        // Not enough space, split table
        const splitIndex = tableRows.length - 3; // Keep last 2 data rows + Total row together
        const firstPart = tableRows.slice(0, splitIndex);
        const secondPart = tableRows.slice(splitIndex);

        // Render First Part
        (doc as any).autoTable({
          startY: currentY + 2,
          head: [
            [
              { content: 'Month', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Claimed Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Drawn Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Difference Payable', colSpan: 6, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Remarks', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } }
            ],
            [
              'PAY', 'D.A.', 'H.R.A', 'TOTAL',
              'PAY', 'D.A', 'H.R.A.', 'TOTAL',
              'PAY', 'D.A', 'H.R.A.', 'GROSS\nAMOUNT', 'NPS/PRAN\nDeduction', 'NET PAY\nAMOUNT'
            ],
            ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16']
          ],
          body: firstPart,
          theme: 'grid',
          styles: { fontSize: 10, fontStyle: 'bold', cellPadding: 1.5, lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0], halign: 'center' },
          headStyles: { fillColor: [255, 228, 225], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [0, 0, 0] },
        });

        doc.addPage();

        // Render Second Part
        (doc as any).autoTable({
          startY: 20, // Start a bit lower on new page
          head: [
            [
              { content: 'Month', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Claimed Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Drawn Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Difference Payable', colSpan: 6, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Remarks', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } }
            ],
            [
              'PAY', 'D.A.', 'H.R.A', 'TOTAL',
              'PAY', 'D.A', 'H.R.A.', 'TOTAL',
              'PAY', 'D.A', 'H.R.A.', 'GROSS\nAMOUNT', 'NPS/PRAN\nDeduction', 'NET PAY\nAMOUNT'
            ],
            ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16']
          ],
          body: secondPart,
          theme: 'grid',
          styles: { fontSize: 10, fontStyle: 'bold', cellPadding: 1.5, lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0], halign: 'center' },
          headStyles: { fillColor: [255, 228, 225], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [0, 0, 0] },
          didParseCell: (data: any) => {
               if (data.row.index === secondPart.length - 1) {
                   data.cell.styles.fontStyle = 'bold';
               }
          }
        });
        finalY = (doc as any).lastAutoTable.finalY + 10;

      } else {
        // Fits normally
        (doc as any).autoTable({
          startY: currentY + 2,
          head: [
            [
              { content: 'Month', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Claimed Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Drawn Amount', colSpan: 4, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Difference Payable', colSpan: 6, styles: { halign: 'center', fillColor: [255, 228, 225] } },
              { content: 'Remarks', rowSpan: 2, styles: { valign: 'middle', halign: 'center', fillColor: [255, 228, 225] } }
            ],
            [
              'PAY', 'D.A.', 'H.R.A', 'TOTAL',
              'PAY', 'D.A', 'H.R.A.', 'TOTAL',
              'PAY', 'D.A', 'H.R.A.', 'GROSS\nAMOUNT', 'NPS/PRAN\nDeduction', 'NET PAY\nAMOUNT'
            ],
            ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16']
          ],
          body: tableRows,
          theme: 'grid',
          styles: { fontSize: 10, fontStyle: 'bold', cellPadding: 1.5, lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0], halign: 'center' },
          headStyles: { fillColor: [255, 228, 225], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [0, 0, 0] },
          didParseCell: (data: any) => {
               if (data.row.index === tableRows.length - 1) {
                   data.cell.styles.fontStyle = 'bold';
               }
          }
        });
        finalY = (doc as any).lastAutoTable.finalY + 10;
      }

      // Footer
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(":- Note-Certified that the amount of this bill has not been paid earlier.", margin, finalY);
      
      const sigY = finalY + 20;
      doc.text("Signature of Teacher", margin, sigY);
      doc.text("Signature & Seal of Headmaster", pageWidth / 2, sigY, { align: "center" });
      doc.text(`Block Education Officer, ${blockName.toUpperCase() || 'GOPALGANJ'}`, pageWidth - margin, sigY, { align: "right" });

      // Add Page Numbers and Watermark
      addWatermarkToPdf(doc);

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        // Add Page Number
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.height - 10, { align: "right" });
      }

      doc.save(`Arrear_${tName || 'Report'}.pdf`);
      setLoading(false);
      onClose();
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const renderMonthList = (type: 'due' | 'drawn') => {
    const data = type === 'due' ? monthlyDue : monthlyDrawn;
    const title = type === 'due' ? "CLAIMED AMOUNT (DUE)" : "PAID AMOUNT (DRAWN)";
    const isManual = type === 'drawn' || claimedPayMode === 'manual';

    return (
      <div className="space-y-10 animate-fadeIn">
        <div className="flex flex-col items-center px-4 gap-6">
           <div className="flex items-center space-x-6 w-full sm:w-auto justify-center">
              <div className="bg-orange-100/80 p-5 rounded-[1.5rem] text-orange-600 shadow-sm">{type === 'due' ? <Calculator size={28} /> : <DollarSign size={28} />}</div>
              <div>
                <h4 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">{title}</h4>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-2">{type === 'due' ? (claimedPayMode === 'auto' ? "Matrix generated values" : "Enter expected salary manually") : "Enter actual received salary details"}</p>
              </div>
           </div>

           {!isAutomatic && type === 'due' && (
              <div className="bg-gray-100 p-1 rounded-[1.8rem] flex items-center shadow-inner w-fit mx-auto">
                <button type="button" onClick={() => setClaimedPayMode('auto')} className={`px-8 py-3 rounded-[1.6rem] text-xs font-black transition-all flex items-center ${claimedPayMode === 'auto' ? 'bg-white text-emerald-600 shadow-md' : 'text-gray-500 hover:text-gray-700'}`}><Zap size={14} className="mr-2" /> AUTO FILL</button>
                <button type="button" onClick={() => setClaimedPayMode('manual')} className={`px-8 py-3 rounded-[1.6rem] text-xs font-black transition-all flex items-center ${claimedPayMode === 'manual' ? 'bg-white text-emerald-600 shadow-md' : 'text-gray-500 hover:text-gray-700'}`}><Edit3 size={14} className="mr-2" /> MANUAL</button>
              </div>
           )}
        </div>

        <div className="grid grid-cols-1 gap-6">
          {data.map((m, idx) => (
            <div key={m.month} className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden transition-all hover:shadow-xl group">
               <button type="button" onClick={() => updateMonth(type, idx, 'isExpanded', !m.isExpanded)} className={`w-full px-8 py-7 flex items-center justify-between transition-colors ${m.isExpanded ? 'bg-orange-50/50' : 'hover:bg-gray-50'}`}>
                 <div className="flex items-center space-x-6">
                    <span className="bg-orange-600 text-white text-[11px] font-black px-4 py-1.5 rounded-lg shadow-lg group-hover:scale-110 transition-transform">{m.month.split('-')[0]}</span>
                    <span className="text-2xl font-black text-gray-800 tracking-tight">{m.monthLabel}</span>
                    <div className="hidden sm:flex items-center text-gray-400 font-bold text-sm ml-8 border-l pl-8 space-x-2">
                       <span className="uppercase text-[10px] tracking-widest opacity-60">NET VALUE:</span>
                       <span className="text-emerald-600 font-black">₹{calculateNetPay(m).toLocaleString()}</span>
                    </div>
                 </div>
                 <div className={`p-2 rounded-full transition-transform ${m.isExpanded ? 'rotate-180 bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'}`}>
                   <ChevronDown size={28} />
                 </div>
               </button>

               {m.isExpanded && (
                 <div className="p-10 pt-4 animate-fadeIn border-t border-orange-100/30">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Basic Pay ({type === 'due' ? 'Expected' : 'Drawn'})</label>
                          <input 
                            type="number" 
                            disabled={type === 'due' && claimedPayMode === 'auto'} 
                            value={m.basic} 
                            onChange={e => updateMonth(type, idx, 'basic', e.target.value)} 
                            onBlur={(e) => {
                              if (type === 'due') {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) {
                                  updateMonth(type, idx, 'basic', (Math.round(val / 10) * 10).toString());
                                }
                              }
                            }}
                            className={`w-full px-6 py-5 border-2 rounded-3xl font-black text-2xl shadow-sm outline-none transition-all ${type === 'due' && claimedPayMode === 'auto' ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-transparent' : 'bg-white border-slate-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10'}`} 
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">D.A. %</label>
                              <div className="relative">
                                <select disabled={type === 'due' && claimedPayMode === 'auto'} value={m.daPercent} onChange={e => updateMonth(type, idx, 'daPercent', e.target.value)} className={`w-full px-4 py-5 border-2 rounded-2xl font-black outline-none appearance-none cursor-pointer shadow-sm transition-all ${type === 'due' && claimedPayMode === 'auto' ? 'bg-gray-100 text-gray-500 border-transparent' : 'bg-white border-slate-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10'}`}>
                                  {DA_OPTIONS.map(rate => <option key={rate} value={rate}>{rate}%</option>)}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-yellow-600"><ChevronDown size={18} /></div>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">D.A. Amount</label>
                              <input 
                                type="number" 
                                disabled={type === 'due' && claimedPayMode === 'auto'} 
                                value={m.da} 
                                onChange={e => updateMonth(type, idx, 'da', e.target.value)} 
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (!isNaN(val)) {
                                    updateMonth(type, idx, 'da', Math.round(val).toString());
                                  }
                                }}
                                className={`w-full px-4 py-5 border-2 rounded-2xl font-black text-xl shadow-sm outline-none transition-all ${type === 'due' && claimedPayMode === 'auto' ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-transparent' : 'bg-white border-slate-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10'}`} 
                              />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">H.R.A. %</label>
                              <div className="relative">
                                <select value={m.hraPercent} onChange={e => updateMonth(type, idx, 'hraPercent', e.target.value)} className="w-full px-4 py-5 bg-white border-2 border-slate-200 rounded-2xl font-black outline-none appearance-none cursor-pointer focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 shadow-sm transition-all">
                                  {(() => {
                                    const [y, m_] = m.month.split('-').map(Number);
                                    const dNum = y * 100 + m_;
                                    let options = (dNum >= 202401 ? HRA_OPTIONS_NEW : HRA_OPTIONS_OLD).map(String);
                                    if (type === 'drawn') {
                                      options = ['4', '5', '6', '7.5', '8', '10', '16', '20'];
                                    }
                                    if (m.hraPercent && !options.includes(m.hraPercent)) {
                                      options.push(m.hraPercent);
                                    }
                                    return options.map(o => <option key={o} value={o}>{o}%</option>);
                                  })()}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-yellow-600"><ChevronDown size={18} /></div>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">H.R.A. Amount</label>
                              <input 
                                type="number" 
                                value={m.hra} 
                                onChange={e => updateMonth(type, idx, 'hra', e.target.value)} 
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (!isNaN(val)) {
                                    updateMonth(type, idx, 'hra', Math.round(val).toString());
                                  }
                                }}
                                className="w-full px-4 py-5 bg-white border-2 border-slate-200 rounded-2xl font-black text-xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 shadow-sm transition-all" 
                              />
                            </div>
                        </div>

                      </div>
                      <div className="space-y-6">
                        <div className="bg-emerald-50 p-6 sm:p-10 rounded-[2.5rem] border border-emerald-100 flex flex-col items-center justify-center shadow-inner relative overflow-hidden group/net text-center h-full">
                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em] mb-4">NET VALUE</span>
                            <span className="text-4xl sm:text-5xl font-black text-emerald-950">₹{calculateNetPay(m).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Copy Tools - Only visible in manual modes */}
                    {isManual && (
                      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 border-t border-gray-50 pt-8">
                        {(m.month.endsWith('-07') || m.month.endsWith('-01')) && (
                          <button 
                            type="button" 
                            onClick={() => {
                              const currentBasic = parseFloat(m.basic) || 0;
                              const incremented = currentBasic * 1.03;
                              const rounded = Math.round(incremented / 10) * 10;
                              updateMonth(type, idx, 'basic', rounded.toString());
                            }}
                            className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-emerald-100 transition-all group"
                          >
                            <TrendingUp size={16} className="group-hover:-translate-y-1 transition-transform" />
                            <span>Apply 3% Increment</span>
                          </button>
                        )}
                        
                        {idx < data.length - 1 && (
                          <>
                            <button 
                              type="button" 
                              onClick={() => copyToNextMonth(type, idx)}
                              className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-blue-100 transition-all group"
                            >
                              <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform" />
                              <span>Copy to Next Month</span>
                            </button>
                            
                            <button 
                              type="button" 
                              onClick={() => copyToAllRemaining(type, idx)}
                              className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all group"
                            >
                              <ChevronsDown size={16} className="group-hover:translate-y-1 transition-transform" />
                              <span>Apply to All Future Months</span>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                 </div>
               )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <LoadingOverlay isLoading={loading || isPaymentProcessing} message={isPaymentProcessing ? "Processing Payment..." : "Generating Arrear Report..."} />
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-0 sm:p-4 bg-black/85 backdrop-blur-md animate-fadeIn">
        <div className="bg-white rounded-none sm:rounded-[3rem] shadow-2xl w-full max-w-6xl overflow-hidden relative border border-gray-100 flex flex-col h-full sm:h-[95vh]">
        <div className="bg-gradient-to-br from-orange-600 to-red-700 p-4 sm:p-8 shrink-0 flex justify-between items-center relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none"><Calculator size={120} className="text-white" /></div>
          <div className="flex items-center space-x-3 sm:space-x-6 relative z-10">
            <button 
              onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onClose()} 
              className="bg-white/20 hover:bg-white/30 p-2 sm:p-4 rounded-full text-white transition-all backdrop-blur-md border border-white/10 shadow-lg group"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="bg-white/20 p-3 sm:p-5 rounded-2xl sm:rounded-3xl backdrop-blur-md border border-white/30 shadow-2xl hidden sm:block">
              <Calculator className="text-white w-6 h-6 sm:w-9 sm:h-9" />
            </div>
            <div>
              <h3 className="text-base sm:text-3xl font-black uppercase tracking-tight leading-tight">{isAutomatic ? 'AREAR AUTOMATIC' : 'AREAR'} CALCULATOR</h3>
              <p className="text-orange-100 font-bold mt-0.5 sm:mt-1 uppercase text-[7px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.4em]">{isAutomatic ? 'Automated Pay Matrix' : 'Official Arrear Format'}</p>
            </div>
          </div>
          <button onClick={onClose} className="bg-red-500/20 hover:bg-red-500/30 text-white p-2 sm:p-4 rounded-full transition-all group relative z-10">
            <X className="w-5 h-5 sm:w-7 sm:h-7" />
          </button>
        </div>
        
        <div id="modal-content" className="flex-grow overflow-y-auto p-4 sm:p-6 md:p-10 no-scrollbar bg-gray-50/50">
        {isAutomatic && (
          <div className="max-w-5xl mx-auto mb-6 sm:mb-8 animate-bounce">
            <div className={`bg-gradient-to-r ${isExperiment ? 'from-emerald-600 to-teal-600' : 'from-orange-600 to-red-600'} p-0.5 sm:p-1 rounded-2xl sm:rounded-[2rem] shadow-xl`}>
              <div className="bg-white/95 backdrop-blur-sm px-4 sm:px-8 py-4 sm:py-6 rounded-[1.4rem] sm:rounded-[1.8rem] flex flex-col sm:flex-row items-center justify-center text-center sm:text-left space-y-3 sm:space-y-0 sm:space-x-6">
                <div className={`${isExperiment ? 'bg-emerald-100 text-emerald-600' : 'bg-orange-100 text-orange-600'} p-3 sm:p-4 rounded-xl sm:rounded-2xl`}>
                  {isExperiment ? <Sparkles size={24} className="sm:w-8 sm:h-8" /> : <RefreshCw size={24} className="animate-spin sm:w-8 sm:h-8" />}
                </div>
                <div>
                  <h4 className="text-lg sm:text-2xl font-black text-gray-900 leading-tight">
                    {isExperiment ? 'Automatic Calculator Active! 🚀' : 'Under Maintenance…😊✌🏼👍🏼'}
                  </h4>
                  <p className="text-gray-500 font-bold text-[10px] sm:text-sm mt-0.5 sm:mt-1 uppercase tracking-widest">
                    {isExperiment ? 'Upload your salary slip to calculate automatically' : 'We will be back with backpack🤣'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
          <form onSubmit={handlePaymentAndGenerate} className="max-w-5xl mx-auto">
            {currentStep === 1 && (
              <div className="space-y-8 sm:space-y-12 animate-fadeIn">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  <div className="md:col-span-2 bg-white p-6 sm:p-10 rounded-2xl sm:rounded-[3rem] shadow-sm border border-gray-100 space-y-4 text-center">
                    <h4 className="text-sm sm:text-base font-black text-slate-950 uppercase tracking-widest flex items-center justify-center"><Building size={16} className="mr-2 text-orange-500 sm:w-5 sm:h-5" /> प्रखण्ड संसाधन केन्द्र का नाम</h4>
                    <input 
                      type="text" 
                      value={blockName} 
                      onChange={e => setBlockName(e.target.value)} 
                      placeholder="ENTER BLOCK NAME (e.g. GOPALGANJ)" 
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white border-2 border-slate-200 rounded-xl sm:rounded-2xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 font-bold transition-all uppercase shadow-sm text-sm sm:text-base text-center" 
                    />
                  </div>

                  {isAutomatic && (
                    <div className="md:col-span-2 bg-white p-6 sm:p-10 rounded-2xl sm:rounded-[3rem] shadow-sm border border-orange-100 space-y-6 sm:space-y-8 bg-gradient-to-br from-white to-orange-50/30">
                      <div className="flex flex-col items-center gap-4 text-center">
                        <h4 className="text-sm sm:text-base font-black text-slate-950 uppercase tracking-widest flex items-center">
                          <Sparkles size={16} className="mr-2 text-orange-500 sm:w-5 sm:h-5" /> महत्वपूर्ण अनुभाग (IMPORTANT SECTION)
                        </h4>
                        <div className="bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] shadow-sm">Required Fields</div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                      {isExperiment ? (
                        <>
                          <div className="space-y-2 sm:space-y-3 text-center">
                            <label className="text-[10px] sm:text-xs font-black text-slate-950 uppercase tracking-widest block">विद्यालय का नाम (SCHOOL NAME)</label>
                            <input 
                              type="text" 
                              value={schoolName} 
                              onChange={e => setSchoolName(e.target.value)} 
                              placeholder="ENTER SCHOOL NAME" 
                              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white border-2 border-slate-200 rounded-xl sm:rounded-2xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 font-bold transition-all uppercase shadow-sm text-sm sm:text-base text-center" 
                            />
                          </div>
                          <div className="space-y-2 sm:space-y-3 text-center">
                            <label className="text-[10px] sm:text-xs font-black text-slate-950 uppercase tracking-widest block">योगदान की तिथि (JOINING DATE)</label>
                            <input 
                              type="date" 
                              value={joiningDate} 
                              onChange={e => setJoiningDate(e.target.value)} 
                              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white border-2 border-slate-200 rounded-xl sm:rounded-2xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 font-bold transition-all uppercase shadow-sm text-sm sm:text-base text-center" 
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="space-y-2 sm:space-y-3 text-center">
                            <label className="text-[10px] sm:text-xs font-black text-slate-950 uppercase tracking-widest block">योगदान की तिथि (JOINING DATE)</label>
                            <input 
                              type="date" 
                              value={joiningDate} 
                              onChange={e => setJoiningDate(e.target.value)} 
                              className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white border-2 border-slate-200 rounded-xl sm:rounded-2xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 font-bold transition-all uppercase shadow-sm text-sm sm:text-base text-center" 
                            />
                          </div>
                        </>
                      )}

                      <div className="space-y-2 sm:space-y-3 text-center">
                        <label className="text-[10px] sm:text-xs font-black text-slate-950 uppercase tracking-widest block">भर्ती चरण (RECRUITMENT PHASE)</label>
                        <div className="relative">
                          <select
                            value={selectedPhase}
                            onChange={(e) => setSelectedPhase(e.target.value)}
                            className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white border-2 border-slate-200 rounded-xl sm:rounded-2xl font-bold outline-none appearance-none cursor-pointer focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all shadow-sm text-sm sm:text-base text-center"
                          >
                            {RECRUITMENT_PHASES.map((phase) => (
                              <option key={phase} value={phase}>{phase}</option>
                            ))}
                          </select>
                          <div className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                            <ChevronDown size={18} className="sm:w-5 sm:h-5" />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 sm:space-y-3 text-center">
                        <label className="text-[10px] sm:text-xs font-black text-slate-950 uppercase tracking-widest block">विषय (SUBJECT)</label>
                        <input 
                          type="text" 
                          value={subject} 
                          onChange={e => setSubject(e.target.value)} 
                          placeholder="e.g. MATH, HINDI" 
                          className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 font-bold transition-all uppercase shadow-sm text-sm sm:text-base text-center" 
                        />
                      </div>
                    </div>
                  </div>
                  )}

                  {isAutomatic && (
                    <div className="md:col-span-2 bg-white rounded-[3rem] border-4 border-orange-500 shadow-2xl overflow-hidden animate-fadeIn">
                      {/* FINANCIAL DETAILS HEADER */}
                      <div className="bg-orange-500 p-8 text-center border-b-4 border-orange-600">
                        <h4 className="text-3xl font-black text-slate-950 uppercase tracking-[0.2em]">
                          वित्तीय विवरण (FINANCIAL DETAILS)
                        </h4>
                      </div>

                      <div className="p-10 space-y-10">
                        <div className="text-center space-y-2">
                          <h5 className="text-xl font-black text-slate-950 uppercase tracking-widest">
                            HRMS वार्षिक वेतन पर्ची अपलोड करें (UPLOAD SALARY SLIPS)
                          </h5>
                          <div className="h-1.5 w-32 bg-orange-500 mx-auto rounded-full"></div>
                        </div>

                        <div className="space-y-8">
                          {['FY 2023-2024', 'FY 2024-2025', 'FY 2025-2026', 'FY 2026-2027'].map((fy) => (
                            <div key={fy} className="group">
                              <div 
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                    const file = e.dataTransfer.files[0];
                                    setSalarySlipFiles(prev => ({ ...prev, [fy]: file }));
                                    parseSalarySlipPDF(file, fy);
                                  }
                                }}
                                onClick={() => {
                                  setFinancialYear(fy);
                                  document.getElementById(`upload-${fy}`)?.click();
                                }}
                                className={`relative border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer flex items-center justify-center gap-3 group ${salarySlipFiles[fy] ? 'border-emerald-400 bg-emerald-50/50' : 'border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50 bg-emerald-50/20'}`}
                              >
                                <input 
                                  id={`upload-${fy}`}
                                  type="file" 
                                  accept=".pdf,image/*" 
                                  className="hidden" 
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                      const file = e.target.files[0];
                                      setSalarySlipFiles(prev => ({ ...prev, [fy]: file }));
                                      parseSalarySlipPDF(file, fy);
                                    }
                                  }}
                                />
                                {parsingYears.includes(fy) ? (
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="animate-spin text-emerald-600" size={20} />
                                    <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest leading-none">UPLOADING...</p>
                                  </div>
                                ) : salarySlipFiles[fy] ? (
                                  <div className="flex items-center justify-between w-full px-2">
                                    <div className="flex items-center gap-3">
                                      <div className="bg-emerald-500 p-2 rounded-lg text-white">
                                        <FileText size={18} />
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs font-bold text-emerald-950 truncate max-w-[150px] leading-tight">{salarySlipFiles[fy]?.name}</p>
                                        <p className="text-[9px] text-emerald-600 font-black uppercase tracking-widest">DATA READY</p>
                                      </div>
                                    </div>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSalarySlipFiles(prev => ({ ...prev, [fy]: null }));
                                      }}
                                      className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                                      title="Remove"
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600 group-hover:bg-emerald-200 group-hover:text-emerald-700 transition-colors">
                                      <Upload size={18} />
                                    </div>
                                    <div className="text-left">
                                      <p className="text-xs font-black text-emerald-800 uppercase tracking-widest group-hover:text-emerald-950 transition-colors leading-tight">UPLOAD {fy}</p>
                                      <p className="text-[9px] text-emerald-500/70 font-bold uppercase tracking-widest">PDF ONLY • MAX 5MB</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="pt-12 border-t-2 border-slate-100 animate-fadeIn flex flex-col items-center">
                          <h5 className="text-3xl sm:text-4xl font-black text-slate-950 uppercase tracking-widest mb-8 flex flex-col items-center gap-4 text-center">
                            <TrendingUp className="text-orange-500" size={48} />
                            HRA में वृद्धि (HRA INCREASE)
                          </h5>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 w-full">
                            <div className="group space-y-4 text-center">
                              <label className="text-sm sm:text-base font-black text-slate-950 uppercase tracking-widest flex items-center justify-center">
                                <div className="w-2 h-5 bg-orange-500 mr-3 rounded-full"></div>
                                किस माह- साल से HRA बढ़ कर मिलना चाहिए़ ?
                              </label>
                              <div className="relative">
                                <input 
                                  type="month" 
                                  value={hraIncreaseMonth} 
                                  onChange={e => setHraIncreaseMonth(e.target.value)} 
                                  className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-3xl font-black text-lg sm:text-xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all uppercase shadow-sm group-hover:border-slate-300 text-center" 
                                />
                              </div>
                            </div>

                            <div className="group space-y-4 text-center">
                              <label className="text-sm sm:text-base font-black text-slate-950 uppercase tracking-widest flex items-center justify-center">
                                <div className="w-2 h-5 bg-orange-500 mr-3 rounded-full"></div>
                                बढ़ा हुआ HRA कितना प्रतिशत (%) है चुने 👇
                              </label>
                              <div className="relative">
                                <select
                                  value={hraIncreasePercent}
                                  onChange={(e) => setHraIncreasePercent(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                  className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-3xl font-black text-lg sm:text-xl outline-none appearance-none cursor-pointer focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all shadow-sm group-hover:border-slate-300 text-center"
                                >
                                  <option value="">SELECT PERCENTAGE</option>
                                  <option value="5">5%</option>
                                  <option value="7.5">7.5%</option>
                                  <option value="10">10%</option>
                                  <option value="20">20%</option>
                                </select>
                                <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                  <ChevronDown size={24} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Extraction Error Message */}
                        {extractionError && (
                          <div className="mt-8 bg-red-50 border-2 border-red-200 rounded-[2rem] p-6 animate-fadeIn flex flex-col sm:flex-row items-center text-center sm:text-left gap-4">
                            <div className="bg-red-100 p-3 rounded-full">
                              <AlertCircle size={24} className="text-red-600" />
                            </div>
                            <div>
                              <h4 className="text-lg font-black text-red-900 uppercase tracking-tight">Data Mismatch Detected</h4>
                              <p className="text-sm text-red-700 font-bold mt-1">{extractionError}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!isAutomatic && (
                    <>
                      <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-gray-100 space-y-8">
                        <h4 className="text-sm sm:text-base font-black text-slate-950 uppercase tracking-widest flex items-center"><User size={16} className="mr-2 text-orange-500" /> मोड और प्रोफाइल (MODE & PROFILE)</h4>
                        <div className="space-y-5">
                          <input type="text" value={tName} onChange={e => setTName(e.target.value)} placeholder="शिक्षक का पूरा नाम (FULL NAME)" className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all" />
                          <input type="text" value={schoolName} onChange={e => setSchoolName(e.target.value)} placeholder="विद्यालय का नाम (SCHOOL NAME)" className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all" />
                          <input type="text" value={pranNo} onChange={e => setPranNo(e.target.value)} placeholder="प्राण नंबर (PRAN NUMBER)" className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all" />
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-950 uppercase tracking-widest ml-1">योगदान की तिथि (JOINING DATE)</label>
                              <input 
                                type="date" 
                                value={joiningDate} 
                                onChange={e => setJoiningDate(e.target.value)} 
                                className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 font-bold transition-all uppercase shadow-sm" 
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-950 uppercase tracking-widest ml-1">भर्ती चरण (RECRUITMENT PHASE)</label>
                              <div className="relative">
                                <select
                                  value={selectedPhase}
                                  onChange={(e) => setSelectedPhase(e.target.value)}
                                  className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold outline-none appearance-none cursor-pointer focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all shadow-sm uppercase"
                                >
                                  {RECRUITMENT_PHASES.map((phase) => (
                                    <option key={phase} value={phase}>{phase}</option>
                                  ))}
                                </select>
                                <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                  <ChevronDown size={20} />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <input 
                            type="text" 
                            value={subject} 
                            onChange={e => setSubject(e.target.value)} 
                            placeholder="SUBJECT (e.g. MATH, HINDI)" 
                            className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 font-bold transition-all uppercase shadow-sm" 
                          />
                        </div>
                      </div>
                      
                      <div className="bg-white p-10 rounded-[3rem] border border-gray-100 shadow-sm space-y-8">
                        <div className="space-y-4">
                          <label className="text-[10px] font-black text-slate-950 uppercase tracking-widest ml-1 flex items-center">
                            <GraduationCap size={14} className="mr-2 text-orange-500" /> शिक्षक ग्रेड / लेवल (TEACHER GRADE)
                          </label>
                          <div className="relative">
                            <select
                              value={selectedGrade}
                              onChange={(e) => setSelectedGrade(e.target.value)}
                              className="w-full px-6 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none appearance-none cursor-pointer transition-all shadow-sm"
                            >
                              {GRADE_LEVELS.map((grade) => (
                                <option key={grade} value={grade}>
                                  Level: {grade}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                              <ChevronDown size={20} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="bg-white p-10 rounded-[3rem] border border-gray-100 shadow-sm text-center">
                    <label className="text-2xl sm:text-3xl font-black text-slate-950 uppercase tracking-widest mb-10 block">बकाया अवधि (ARREAR PERIOD)</label>
                    <div className="grid grid-cols-1 gap-10">
                      <div className="space-y-4">
                        <span className="text-sm sm:text-base font-black text-slate-950 uppercase tracking-widest block">कब से (FROM MONTH)</span>
                        <input type="month" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-3xl font-black text-lg sm:text-xl text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all shadow-sm text-center" />
                      </div>
                      <div className="space-y-4">
                        <span className="text-sm sm:text-base font-black text-slate-950 uppercase tracking-widest block">कब तक (TO MONTH)</span>
                        <input type="month" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-6 py-5 bg-white border-2 border-slate-200 rounded-3xl font-black text-lg sm:text-xl text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all shadow-sm text-center" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-10">
                  {isAutomatic ? (
                    <div className="w-full space-y-4">
                      <button 
                        disabled={loading} 
                        type="button" 
                        onClick={handleGenerate}
                        className={`w-full py-8 rounded-[2.5rem] text-white font-black text-2xl shadow-2xl transition-all flex items-center justify-center group transform active:scale-[0.98] ${loading || udiseStatus === 'searching' ? 'bg-gray-400 cursor-not-allowed' : 'bg-slate-900 hover:bg-black'}`}
                      >
                        {loading ? <Loader2 className="animate-spin mr-4" size={32} /> : <FileDown size={32} className="mr-4 group-hover:translate-y-1 transition-transform" />}
                        GENERATE ARREAR PDF
                      </button>
                      <button 
                        disabled={isPaymentProcessing || loading} 
                        type="button" 
                        onClick={handlePaymentAndGenerate}
                        className={`w-full py-8 rounded-[2.5rem] text-white font-black text-2xl shadow-2xl transition-all flex items-center justify-center group transform active:scale-[0.98] ${isPaymentProcessing || loading || udiseStatus === 'searching' ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'}`}
                      >
                        {isPaymentProcessing ? <RefreshCw className="animate-spin mr-4" size={32} /> : <CreditCard size={32} className="mr-4 group-hover:translate-y-1 transition-transform" />}
                        PAYMENT
                      </button>
                      <p className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-6">
                        Secure Verification System
                      </p>
                    </div>
                  ) : (
                    <button 
                      type="button" 
                      onClick={() => {
                        if (udiseStatus === 'searching') return;
                        setCurrentStep(2);
                      }} 
                      className={`bg-slate-900 hover:bg-black text-white px-16 py-7 rounded-[3rem] font-black text-2xl shadow-2xl transition-all flex items-center group ${udiseStatus === 'searching' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      PROCEED TO CLAIM AMOUNT DATA <ChevronRight size={32} className="ml-3 group-hover:translate-x-2 transition-transform" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {currentStep === 2 && !isAutomatic && (
              <div className="space-y-12 animate-fadeIn">
                {renderMonthList('due')}
                <div className="flex flex-col md:flex-row gap-6 pt-10">
                  <button type="button" onClick={() => setCurrentStep(1)} className="flex-1 py-8 rounded-[3rem] bg-white border-2 border-slate-100 text-slate-500 font-black text-xl flex items-center justify-center transition-all shadow-md active:scale-95"><ArrowLeft className="mr-3" /> GO BACK</button>
                  <button 
                    type="button" 
                    onClick={() => setCurrentStep(3)} 
                    className="flex-[2] py-8 rounded-[3.5rem] bg-slate-900 hover:bg-black text-white font-black text-2xl shadow-2xl transition-all flex items-center justify-center transform active:scale-[0.97]"
                  >
                    PROCEED TO DRAWN DATA <ChevronRight size={32} className="ml-3 group-hover:translate-x-2 transition-transform" />
                  </button>
                </div>
              </div>
            )}

            {currentStep === 3 && !isAutomatic && (
              <div className="space-y-12 animate-fadeIn">
                {renderMonthList('drawn')}
                <div className="flex flex-col md:flex-row gap-6 pt-10">
                  <button type="button" onClick={() => setCurrentStep(2)} className="flex-1 py-8 rounded-[3rem] bg-white border-2 border-slate-100 text-slate-500 font-black text-xl flex items-center justify-center transition-all shadow-md active:scale-95"><ArrowLeft className="mr-3" /> GO BACK</button>
                  <button disabled={loading || isPaymentProcessing} type="submit" className={`flex-[2] py-8 rounded-[3.5rem] text-white font-black text-3xl shadow-2xl transition-all flex items-center justify-center transform active:scale-[0.97] ${(loading || isPaymentProcessing) ? 'bg-gray-300' : 'bg-gradient-to-r from-orange-600 to-red-600'}`}>
                    {(loading || isPaymentProcessing) ? <Loader2 className="animate-spin mr-5" /> : <FileDown className="mr-5" size={36} />} {(loading || isPaymentProcessing) ? 'PROCESSING...' : 'PAY ₹20 & GENERATE ARREAR BILL'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
    </>
  );
};